import he from 'he';
import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

type ContentType = string | ReactNode | Array<string | ReactNode>;

/**
 * This function is used to recursively decode HTML entities (e.g. &amp;, &lt;, &quot;)
 * in dynamic content that may be deeply nested.
 *
 * The input can be:
 * - a plain string (to decode directly),
 * - a React element (to decode its children),
 * - or an array containing any mix of strings and elements.
 *
 * It ensures that all strings anywhere in the structure are decoded properly,
 * without breaking JSX elements or layout.
 */
export const decodeHTML = (content: ContentType): ContentType => {
  if (typeof content === 'string') {
    return he.decode(content);
  }

  if (Array.isArray(content)) {
    return content.map((item) => decodeHTML(item));
  }

  if (isValidElement(content)) {
    const element = content as ReactElement<PropsWithChildren>;

    const decodedChildren = decodeHTML(element.props.children);

    return cloneElement(element, {
      ...element.props,
      children: decodedChildren as ReactNode,
    });
  }

  return content;
};
